import { z } from "zod";
import { defineStepConfig } from "../../../registry/helpers";
import { entityOfType } from "../../../utils/system-entities";
import { SystemEntity } from "@erp/shared-types";

/**
 * A date-only value. Accepts `YYYY-MM-DD` or a full ISO timestamp (from a
 * timestamp socket), which is cut to its date part.
 */
const dateOnlyValue = z.union([
  z.iso.date(),
  z.iso
    .datetime({ offset: true })
    .transform((timestamp) => timestamp.slice(0, 10)),
]);

const nonBlankString = z.string().trim().min(1);

/**
 * The invoice header fields a workflow may set, each with the schema its
 * value must satisfy. `null` clears `dueDate` / `preferredPayDate`; the other
 * fields can only be set.
 *
 * Amounts, supplier and status are deliberately absent: they move money or
 * change who is paid, and have their own validated paths (line edits,
 * matching, approval).
 */
export const AP_INVOICE_UPDATABLE_FIELD_SCHEMAS = {
  invoiceDate: dateOnlyValue,
  dueDate: dateOnlyValue.nullable(),
  preferredPayDate: dateOnlyValue.nullable(),
  paymentTerms: nonBlankString,
  invoiceNumber: nonBlankString,
  referenceIds: z.array(nonBlankString),
  notes: z.string(),
} as const;

export const APInvoiceUpdatableFieldSchema = z.enum([
  "invoiceDate",
  "dueDate",
  "preferredPayDate",
  "paymentTerms",
  "invoiceNumber",
  "referenceIds",
  "notes",
]);

export type APInvoiceUpdatableField = z.infer<
  typeof APInvoiceUpdatableFieldSchema
>;

/** Fields whose change re-derives the due date from payment terms. */
export const AP_INVOICE_DUE_DATE_DRIVER_FIELDS: readonly APInvoiceUpdatableField[] =
  ["invoiceDate", "paymentTerms"];

export const UpdateAPInvoiceFieldSkipReasonSchema = z.enum([
  "not_draft",
  "no_value",
  "unchanged",
]);

/**
 * Update AP Invoice Field Step
 *
 * Sets one header field on a DRAFT AP invoice to a wired value — e.g. set the
 * invoice date from another date on the invoice for suppliers that need it:
 *
 * ```
 * decompose_entity(AP invoice) ─ supplierId ─→ Switch(case: supplierId in [...])
 *                                                 └─ [case] → update_ap_invoice_field.__flow_control_in__
 * decompose_entity(AP invoice) ─ receivedAt ─────────────→ update_ap_invoice_field.value
 *                                                          (config: field = invoiceDate)
 * ```
 *
 * Only DRAFT invoices are written. Other statuses are skipped (`updated:
 * false`), or fail the step when `failIfNotDraft` is set. Changing the invoice
 * date or payment terms re-derives the due date from the terms. The write is
 * recorded in the invoice's Activity history against the workflow run and
 * step, with the optional `reason`.
 */
export const updateAPInvoiceFieldStep = defineStepConfig({
  type: "update_ap_invoice_field",
  name: "Update AP Invoice Field",
  description:
    "Set one header field on a draft AP invoice (dates, terms, number, references, notes), recorded in the invoice's history",
  category: "Data Access",
  libraryModule: "AccountsPayable",

  inputSchema: z.object({
    apInvoice: entityOfType(SystemEntity.AP_INVOICE).describe(
      "The AP invoice to update",
    ),
    value: z
      .any()
      .optional()
      .describe(
        "The new value. Dates take YYYY-MM-DD; null clears dueDate / preferredPayDate. When unset, nothing is written.",
      ),
    reason: z
      .string()
      .optional()
      .describe("Why the field was changed; shown in the invoice's history"),
  }),

  outputSchema: z.object({
    apInvoice: entityOfType(SystemEntity.AP_INVOICE).describe(
      "The invoice, for chaining further steps",
    ),
    updated: z.boolean().describe("Whether the field was written"),
    previousValue: z
      .any()
      .optional()
      .describe("The field's value before this step"),
    newValue: z.any().optional().describe("The field's value after this step"),
    dueDate: z.iso
      .date()
      .optional()
      .describe("The invoice's due date after this step (YYYY-MM-DD)"),
    skipReason: UpdateAPInvoiceFieldSkipReasonSchema.optional().describe(
      "Why nothing was written: not_draft, no_value or unchanged",
    ),
  }),

  configSchema: z.object({
    field: APInvoiceUpdatableFieldSchema.describe(
      "The invoice header field to set",
    ),
    failIfNotDraft: z
      .boolean()
      .default(false)
      .describe(
        "Fail the step when the invoice is not a draft (default: skip it)",
      ),
  }),

  emits: [] as const,
});
